import { ErrorSidePanel } from '@/components/errors';
import { useForm } from '@deadcow-enterprises/react-forms';
import { logger, templates, useI18n, type SkinPageConfig } from '@nosinovacao/nosid-mfe-common';
import { createFileRoute } from '@tanstack/react-router';
import type { HistoryState } from '@tanstack/react-router';
import z from 'zod';
import { useValidatePassword } from '../-hooks/password.hooks';
import { type ChangePasswordProps } from './-model/props';
import { ChangePasswordLight, ChangePasswordWooDark, ChangePasswordWoo } from './-skins';

export const Route = createFileRoute(
  '/(home)/home/(change-password)/_sheet/change-password/(landing-page)/',
)({
  component: RouteComponent,
  errorComponent: ErrorSidePanel,
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const { t } = useI18n();

  const form = useForm<{ password: string }>(
    {
      password: {
        value: '',
        id: 'current-password',
        validator: z.string().min(1, t('Home.ChangePassword.Errors.PasswordRequired')),
      },
    },
    { onError: logger.logFormErrors.bind(logger) },
  );

  const { mutate: validatePassword, isPending } = useValidatePassword({
    onSuccess: () => {
      void navigate({
        to: '/home/change-password/new-password',
        state: { oldPassword: form.value.password } as HistoryState,
      });
    },
    onError: () => {
      form.addError('password', t('Home.ChangePassword.Errors.PasswordInvalid'));
    },
  });

  const handleSubmit = ({ password }: typeof form.value) => {
    validatePassword(password);
  };

  const handleForgotPassword = () => {
    void navigate({ to: '/home/change-password/contact-selection' });
  };

  const props: ChangePasswordProps = {
    form,
    isPending,
    onSubmit: handleSubmit,
    onForgotPassword: handleForgotPassword,
  };

  return templates.getSkinSelector(skinConfig(props));
}

function skinConfig(props: ChangePasswordProps): SkinPageConfig {
  return {
    skins: [
      {
        selector: 'isWooTvTemplate',
        component: <ChangePasswordWooDark {...props} />,
      },
      {
        selector: 'isWooTemplate',
        component: <ChangePasswordWoo {...props} />,
      },
    ],
    defaultValue: <ChangePasswordLight {...props} />,
  };
}
